import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';

/**
 * Shows nested delivery messaging content when variant price meets the threshold.
 * Uses display: contents when visible so the inner group layout is unaffected.
 *
 * @extends {Component}
 */
class DeliveryMessaging extends Component {
  /** @type {Record<string, number>} */
  #prices = {};

  /** @type {number} */
  #threshold = 11000;

  connectedCallback() {
    super.connectedCallback();

    this.#threshold = Number(this.dataset.threshold || 11000);
    this.#prices = this.#readPrices();

    const target = this.closest('[id*="ProductInformation-"], [id*="QuickAdd-"], .shopify-section');
    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);

    this.#sync(Number(this.dataset.price || 0));
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const target = this.closest('[id*="ProductInformation-"], [id*="QuickAdd-"], .shopify-section');
    target?.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  /**
   * @returns {Record<string, number>}
   */
  #readPrices() {
    const scriptId = this.dataset.pricesScript;
    if (!scriptId) return {};

    const script = document.getElementById(scriptId);
    if (!(script instanceof HTMLScriptElement)) return {};

    try {
      return JSON.parse(script.textContent || '{}');
    } catch {
      return {};
    }
  }

  /**
   * @param {import('@shopify/events').ProductSelectEvent} event
   */
  #handleProductSelect = (event) => {
    event.promise
      .then(({ detail }) => {
        if (!detail) return;

        const { newProduct, resource, productId } = detail;

        if (newProduct) {
          this.dataset.productId = newProduct.id;
        }

        if (productId && productId !== this.dataset.productId) {
          return;
        }

        let price = 0;

        if (resource?.price != null) {
          price = Number(resource.price);
        } else if (resource?.id != null && this.#prices[String(resource.id)] != null) {
          price = Number(this.#prices[String(resource.id)]);
        }

        if (resource?.id != null) {
          this.dataset.variantId = String(resource.id);
        }

        this.dataset.price = String(price);
        this.#sync(price);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') {
          console.warn('[delivery-messaging] Event promise rejected:', error);
        }
      });
  };

  /**
   * @param {number} price
   */
  #sync(price) {
    if (!this.dataset.threshold) return;

    const eligible = Number.isFinite(price) && price >= this.#threshold;

    this.hidden = !eligible;
    this.style.display = eligible ? 'contents' : 'none';
  }
}

if (!customElements.get('delivery-messaging')) {
  customElements.define('delivery-messaging', DeliveryMessaging);
}
